import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ViewChild,
  ElementRef,
   AfterViewInit
} from '@angular/core';
import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
   ColumnMode,
   DatatableComponent,
   DataTableColumnCellDirective,
   DataTableColumnDirective
} from '@swimlane/ngx-datatable';
import { ShareholderService } from '@appShared/services/shareholder.service';
import { AppFacade } from '@appShared/services/app.facade';
import { ShareholderFacade } from '@appShared/services/shareholder.facade';
import { IProfile } from '@appShared/interfaces/[Interface-based]/profile.interface';
import { IShareholder } from '@appShared/interfaces/[Model-based]/shareholder.interface';
import { IShareholderSummaryInfo } from '@appShared/interfaces/[Model-based]/shareholder-summary-info.interface';
import { ShareholderEditModalComponent } from '@appComponents/shareholder-edit/shareholder-edit-modal/shareholder-edit-modal.component';
import { StockListModalComponent } from '@appComponents/stock/stock-list/stock-list-modal.component';
import { ConfirmModalComponent, ConfirmTemplateDirective } from '@appShared/components';

@Component({
  selector: 'app-shareholder-list',
  templateUrl: './shareholder-list.component.html',
  styleUrls: ['./shareholder-list.component.less'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [
      FormsModule,
      AsyncPipe,
      CurrencyPipe,
      DecimalPipe,
      DatatableComponent,
      DataTableColumnDirective,
      DataTableColumnCellDirective,
      ConfirmTemplateDirective,
      ConfirmModalComponent
   ]
})
export class ShareholderListComponent implements OnInit, AfterViewInit {
  userProfile$: Observable<IProfile>;
  @ViewChild('filterInput', { static: true })
  filterElementRef: ElementRef;
  @ViewChild(DatatableComponent, { static: true })
  table: DatatableComponent;
  canEdit: boolean;
  filteredShareholders$: Observable<IShareholderSummaryInfo[]>;
  shareholderSearchFilter$: Observable<string>;
  includeRepurchaseOptionExecuted: boolean;
  private _searchFilter: string;
  hitMessage: string;
  /* grid */
  ColumnMode = ColumnMode;

  constructor(
    private _appFacade: AppFacade,
    private _shareholderFacade: ShareholderFacade,
    private _shareholderService: ShareholderService,
    private _modalService: NgbModal
  ) {
    this.userProfile$ = this._appFacade.userProfile$.pipe(
      tap(userProfile => (this.canEdit = userProfile.isAdmin))
    );
  }

  ngOnInit(): void {
    this.shareholderSearchFilter$ = this._shareholderFacade.shareholderSearchFilter$.pipe(
      tap(searchFilter => (this._searchFilter = searchFilter))
    );

    this.filteredShareholders$ = this._shareholderFacade.filteredShareholders$.pipe(
      tap(filteredShareholders => {
        if (filteredShareholders) {
          const hitCount = filteredShareholders.length;
          if (hitCount) {
            this.hitMessage = this._searchFilter
              ? `${hitCount} records found matching: '${this._searchFilter}'`
              : '';
          } else {
            this.hitMessage = `No records found matching: '${
              this._searchFilter
            }'`;
          }
        }
      })
    );
  }

  ngAfterViewInit(): void {
    // this.filterInput.valueChanges.subscribe(
    //  () => this.performFilter(this.listFilter)
    //  );
    if (this.filterElementRef) {
      this.filterElementRef.nativeElement.focus();
    }
  }

  /*
   * private methods
   * */

  private _showShareholderEditModal(
    shareholder: IShareholderSummaryInfo
  ): void {
    const shareholderEditModalRef = this._modalService.open(
      ShareholderEditModalComponent,
      { scrollable: true, windowClass: 'my-modal-lg' }
    );
    const shareholderEditComponent = shareholderEditModalRef.componentInstance;
    shareholderEditComponent.shareholder = {
      id: shareholder.id,
      firstName: shareholder.firstName,
      lastName: shareholder.lastName,
      repurchaseOptionAsOf: shareholder.repurchaseOptionAsOf
    } as IShareholder;
    shareholderEditComponent.canEdit = this.canEdit;
    shareholderEditComponent.refreshGrid = this.refreshGrid.bind(this);
  }

  /*
   * public methods
   * */

  setSearchFilter(searchFilter: string): void {
    this._shareholderFacade.setShareholderSearchFilter(searchFilter);
  }

  setRepurchaseOptionAsOf(includeRepurchaseOptionExecuted?: boolean) {
    this._shareholderFacade.loadShareholders(includeRepurchaseOptionExecuted);
  }

  editShareholder(shareholder: IShareholderSummaryInfo, evt): void {
    /*
     adding this blur method to resolve known ngx-datatable issue and NgbModal
     https://github.com/swimlane/ngx-datatable/issues/721#issuecomment-389634571
     "ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.
      Previous value: 'datatable-body-cell custom sort-active active'"
    */
    evt.target.parentElement.parentElement.blur();

    this._showShareholderEditModal({ ...shareholder });
  }

  createShareholder(): void {
    this._showShareholderEditModal({
      id: 0
    } as IShareholderSummaryInfo);
  }

  viewReport(shareholder: IShareholder): void {
    this._shareholderService.generatePlanPdf(shareholder);
  }

  refreshGrid(): void {
    this._shareholderFacade.loadShareholders(this.includeRepurchaseOptionExecuted);
  }

  showStocks(): void {
    this._modalService.open(StockListModalComponent, {
      scrollable: true
      // windowClass: 'my-modal-md'
    });
  }
}
